import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CardMedia,
} from '@mui/material';
import { FontIcon } from './FontIcon';
import type { Image as Interface, FileMetadata } from '../../design/components';
import { isValidImageSource, isFileMetadata, downloadImage } from '../shared/image';

export const Image: React.FC<Interface> = ({
  name,
  description,
  state,
  placeholder = 'No image selected',
  hidden,
  inline,
  readonly: _readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['' as unknown as FileMetadata, () => {}];
  const [expanded, setExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isValid = isValidImageSource(value);

  const displayUrl = useMemo(() => isValid ? (value as FileMetadata).url : '', [value, isValid]);

  useEffect(() => {
    return () => { if (displayUrl.startsWith('blob:')) URL.revokeObjectURL(displayUrl); };
  }, [displayUrl]);

  const handleDownload = () => {
    if (isFileMetadata(value)) downloadImage(value.url, value.name);
  };

  const processFile = async (file: File) => {
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.src = url;
      await img.decode();
      setValue({
        url, name: file.name, type: file.type, size: file.size,
        modified: new Date(file.lastModified).toISOString(),
        pages: 1, width: img.naturalWidth, height: img.naturalHeight, file,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file).catch(() => {});
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file).catch(() => {});
  };

  const controlsHeight = 40;
  const previewHeight = 300;
  const containerHeight = previewHeight + controlsHeight;

  if (inline) {
    return value ? (
      <Box sx={{ display: hidden ? 'none' : 'inline-block' }}>
        <img
          src={displayUrl}
          alt={name || 'Image'}
          style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
        />
      </Box>
    ) : null;
  }

  const canUpload = !_readonly && !disabled;

  return (
    <Box sx={{ display: hidden ? 'none' : undefined, mb: 2 }}>
      {name && (
        <Typography
          component="label"
          variant="body2"
          sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
        >
          {name}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      )}

      {/* Image Preview */}
      {isValid && (
        <Box
          sx={{
            width: '100%',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            overflow: expanded ? 'auto' : 'hidden',
            height: expanded ? undefined : containerHeight,
            maxHeight: expanded ? '80vh' : undefined,
            opacity: disabled ? 0.5 : 1,
          }}
        >
          {/* Controls */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Tooltip title={expanded ? 'Shrink' : 'Expand'}>
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
              >
                <FontIcon name={expanded ? 'FullscreenExit' : 'Fullscreen'} size="small" />
              </IconButton>
            </Tooltip>

            {isFileMetadata(value) && (
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  component="a"
                  href={value.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontIcon name="OpenInNew" size="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={handleDownload}
              >
                <FontIcon name="Download" size="small" />
              </IconButton>
            </Tooltip>

            {canUpload && (
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => setValue('' as unknown as FileMetadata)}
                >
                  <FontIcon name="Delete" size="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CardMedia
              component="img"
              image={displayUrl}
              alt={name || 'Image'}
              sx={{
                maxHeight: expanded ? undefined : previewHeight,
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      )}

      {/* Upload zone - empty state */}
      {!isValid && canUpload && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 96,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: dragOver ? 'primary.main' : error ? 'error.main' : 'divider',
            bgcolor: dragOver ? 'primary.50' : 'action.hover',
            color: 'text.secondary',
            cursor: 'pointer',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <FontIcon name="Upload" />
          <Typography variant="body2" sx={{ mt: 0.5 }}>Click or drag to upload image</Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </Box>
      )}

      {/* Static empty state for readonly/disabled */}
      {!isValid && !canUpload && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 96,
            borderRadius: 1,
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            bgcolor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <FontIcon name="Image" />
          <Typography sx={{ ml: 1 }}>{placeholder}</Typography>
        </Box>
      )}

      {(error || description) && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 0.5, color: error ? 'error.main' : 'text.secondary' }}
        >
          {error || description}
        </Typography>
      )}
    </Box>
  );
};
